import React from 'react';
import { Badge } from './ui/badge';

const tenants = [
  {
    name: 'Meridian Capital Group',
    building: 'Tower A',
    options: ['ROFR', 'Renewal'],
    status: 'Active',
    daysUntil: 42,
    camCap: '3.5%',
    verified: 'Feb 3, 2026',
  },
  {
    name: 'Atlas Logistics Inc.',
    building: 'Building C',
    options: ['Expansion', 'ROFR'],
    status: 'Active',
    daysUntil: 18,
    camCap: '4.0%',
    verified: 'Jan 28, 2026',
  },
  {
    name: 'Horizon Healthcare',
    building: 'Tower B',
    options: ['Renewal'],
    status: 'Waived',
    daysUntil: null,
    camCap: '3.0%',
    verified: 'Jan 15, 2026',
  },
  {
    name: 'Summit Retail Corp.',
    building: 'Building A',
    options: ['ROFR', 'Expansion', 'Renewal'],
    status: 'Active',
    daysUntil: 7,
    camCap: '5.0%',
    verified: 'Feb 10, 2026',
  },
  {
    name: 'Pinnacle Financial',
    building: 'Tower A',
    options: ['Renewal'],
    status: 'Expired',
    daysUntil: null,
    camCap: '3.2%',
    verified: 'Dec 19, 2025',
  },
  {
    name: 'Cascade Properties LLC',
    building: 'Building D',
    options: ['ROFR', 'Expansion'],
    status: 'Active',
    daysUntil: 31,
    camCap: '4.5%',
    verified: 'Feb 1, 2026',
  },
  {
    name: 'Sterling Law Partners',
    building: 'Tower B',
    options: ['Renewal', 'ROFR'],
    status: 'Active',
    daysUntil: 12,
    camCap: '3.8%',
    verified: 'Feb 8, 2026',
  },
];

function OptionChip({ type }: { type: string }): JSX.Element {
  const colorMap: Record<string, string | undefined> = {
    ROFR: 'bg-teal/40 text-foreground',
    Renewal: 'bg-[#E8D5F5] text-foreground',
    Expansion: 'bg-[#FDE8D0] text-foreground',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium ${colorMap[type] ?? 'bg-muted text-muted-foreground'}`}>
      {type}
    </span>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const colorMap: Record<string, string | undefined> = {
    Active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Waived: 'bg-amber-50 text-amber-700 border-amber-200',
    Expired: 'bg-red-50 text-red-600 border-red-200',
  };
  return (
    <Badge variant='outline' className={`rounded-full text-[11px] font-medium ${colorMap[status] ?? ''}`}>
      {status}
    </Badge>
  );
}

export function ProductMockupLarge(): JSX.Element {
  return (
    <div className='rounded-xl border border-border bg-card shadow-xl overflow-hidden'>
      {/* Top bar */}
      <div className='flex flex-col gap-3 border-b border-border bg-muted/50 px-5 py-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <div className='h-2.5 w-2.5 rounded-full bg-teal' />
            <span className='text-sm font-semibold text-foreground tracking-tight'>Portfolio Lease Risk Dashboard</span>
          </div>
          <div className='flex items-center gap-3'>
            <span className='text-[11px] text-muted-foreground'>7 tenants</span>
            <span className='text-[11px] text-muted-foreground'>4 buildings</span>
          </div>
        </div>
        <div className='flex items-center gap-2'>
          <select disabled className='rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground pointer-events-none select-none cursor-default' aria-label='Filter by building'>
            <option>All Buildings</option>
          </select>
          <select disabled className='rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground pointer-events-none select-none cursor-default' aria-label='Filter by option type'>
            <option>All Option Types</option>
          </select>
          <select disabled className='rounded-md border border-border bg-card px-2.5 py-1 text-[11px] text-muted-foreground pointer-events-none select-none cursor-default' aria-label='Filter by status'>
            <option>All Statuses</option>
          </select>
          <div className='ml-auto flex items-center gap-1.5'>
            <div className='h-3.5 w-7 rounded-full bg-teal relative'>
              <div className='absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-card' />
            </div>
            <span className='text-[10px] text-muted-foreground font-medium'>Active Only</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='w-full overflow-x-auto md:overflow-visible'>
        <table className='w-full text-left text-xs'>
          <thead>
            <tr className='border-b border-border bg-muted/30'>
              <th className='px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap'>Tenant</th>
              <th className='px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap'>Building</th>
              <th className='px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap'>Option Types</th>
              <th className='px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap'>Status</th>
              <th className='px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap'>Window Opens In</th>
              <th className='px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap'>CAM Cap</th>
              <th className='px-3 py-3 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap'>Last Verified</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map((t) => (
              <tr key={t.name} className='border-b border-border/50 last:border-0 hover:bg-muted/20 transition-colors'>
                <td className='px-3 py-3 whitespace-nowrap'>
                  <span className='font-medium text-foreground text-xs'>{t.name}</span>
                </td>
                <td className='px-3 py-3 text-xs text-muted-foreground whitespace-nowrap'>{t.building}</td>
                <td className='px-3 py-3'>
                  <div className='flex flex-nowrap gap-1 whitespace-nowrap'>
                    {t.options.map((opt) => (
                      <OptionChip key={opt} type={opt} />
                    ))}
                  </div>
                </td>
                <td className='px-3 py-3 whitespace-nowrap'>
                  <StatusBadge status={t.status} />
                </td>
                <td className='px-3 py-3 whitespace-nowrap'>
                  {t.daysUntil !== null ? (
                    <span className={`text-xs font-medium ${t.daysUntil <= 14 ? 'text-red-600 font-semibold' : 'text-foreground'}`}>
                      {t.daysUntil}
                      {' '}
                      days
                    </span>
                  ) : (
                    <span className='text-xs text-muted-foreground'>&mdash;</span>
                  )}
                </td>
                <td className='px-3 py-3 text-xs text-foreground font-medium whitespace-nowrap'>{t.camCap}</td>
                <td className='px-3 py-3 text-[11px] text-muted-foreground whitespace-nowrap'>{t.verified}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
